import type { ButtonHTMLAttributes, CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { useEffect, useState } from 'react';

export type ButtonProps = {
	blank?: boolean;
	children: ReactNode;
	danger?: boolean;
	disabled?: boolean;
	hasCountdown?: boolean;
	hasLoader?: boolean;
	href?: string;
	id?: string;
	loading?: boolean;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	primary?: boolean;
	specifier?: string;
	sso?: boolean;
	ssoId?: string;
	tertiary?: boolean;
	testId?: string;
	type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

export interface CountdownButtonProps extends Omit<ButtonProps, 'onClick'> {
	duration: number;
	onClick: () => void;
}

export function CountdownButton(props: CountdownButtonProps) {
	const durationInMilliseconds = props.duration * 1000; // Convert seconds to milliseconds
	const interval = 50; // Update every 50ms
	const [remainingTime, setRemainingTime] = useState(durationInMilliseconds);

	const degree = Math.round((360 / durationInMilliseconds) * remainingTime);
	const remainingTimeInSeconds = Math.ceil(remainingTime / 1000);
	const progressStyle: CSSProperties & { '--progress': string } = { '--progress': `${degree}deg` };

	useEffect(() => {
		let timer: ReturnType<typeof setInterval> | undefined;

		if (remainingTime > 0) {
			timer = setInterval(() => {
				setRemainingTime((prevTime) => {
					if (prevTime <= 0) {
						clearInterval(timer);
						return 0;
					}
					return prevTime - interval;
				});
			}, interval);
		}

		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (remainingTime <= 0) {
			props.onClick?.();
		}
	}, [remainingTime]);

	const onCountdownButtonClick = () => setRemainingTime(0);

	return remainingTime > 0 ? (
		<Button {...props} hasCountdown={true} onClick={onCountdownButtonClick}>
			{props.children}
			<span style={progressStyle}>{remainingTimeInSeconds}s</span>
		</Button>
	) : (
		<LoadingButton {...props} loading={true} />
	);
}

export function LoadingButton(props: ButtonProps) {
	return <Button {...props} hasLoader={true} />;
}

export function PrimaryButton(props: ButtonProps) {
	return <Button {...props} primary={true} />;
}

export function TertiaryButton(props: ButtonProps) {
	return <Button {...props} tertiary={true} />;
}

export function DangerButton(props: ButtonProps) {
	return <Button {...props} danger={true} />;
}

export default function Button({
	blank = false,
	children,
	danger = false,
	disabled = false,
	hasCountdown = false,
	hasLoader = false,
	href = '',
	id = '',
	loading = false,
	onClick = () => {},
	primary = false,
	specifier = '',
	sso = false,
	ssoId = '',
	tertiary = false,
	testId = '',
	type = 'button',
}: ButtonProps) {
	const props = {
		disabled,
	};

	const classes = ['btn'];
	if (primary) classes.push('btn--primary');
	if (tertiary) classes.push('btn--tertiary');
	if (danger) classes.push('btn--danger');
	if (specifier) classes.push(`btn--${specifier}`);

	if (hasCountdown) classes.push('btn--countdown');
	if (hasLoader) classes.push('btn--loader');
	if (loading) classes.push('btn--loading');

	// Handle SSO-specific classes
	if (sso) {
		classes.push('btn--sso');

		if (ssoId) {
			classes.push(`btn--sso--${ssoId}`);
		}
	}

	const elementProps = {
		...props,
		className: classes.join(' '),
		...(id ? { id } : {}),
		...(testId ? { 'data-testid': testId } : {}),
	};

	if (href) {
		return (
			<a {...elementProps} href={href} {...(blank ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
				{hasLoader ? <span>{children}</span> : children}
			</a>
		);
	} else {
		return (
			<button {...elementProps} type={type} onClick={onClick}>
				{hasLoader ? <span>{children}</span> : children}
			</button>
		);
	}
}
